// Browser-side OBS scene-switching service — the ObsService of
// docs/OBS_SCENE_SWITCHING_BRIEF.md, relocated from the specced .NET server
// into the TO's own browser (the only place on our architecture that can
// reach ws://localhost:4455 — Cloudflare Functions never can).
//
// Spec parity, translated:
//   ConnectAsync            → connect()                (idempotent)
//   SetSceneAsync           → setScene(key)            → Promise<bool>
//   PreviewAndTakeAsync     → previewAndTake(key)      → Promise<bool>
//   IsConnected             → isConnected              (getter)
//   GetScenesAsync          → refreshScenes()          → Promise<string[]>
//   BackgroundService retry → _scheduleReconnect()     (backoff below)
//   Enabled=false no-op     → status 'disabled'; every command logs + false
//
// Module-level singleton on purpose: the spec's "a single OBSWebsocket
// instance shared across concurrent requests" plus React StrictMode's
// double-mounted effects both want exactly one connection owner. Commands
// NEVER throw — they resolve false and append to the activity log, mirroring
// the "never throw into the request pipeline" rule.
//
// Config lives in localStorage, deliberately not in package_state: the OBS
// websocket password is a machine-local secret for a machine-local socket —
// syncing it through the server would copy it to every device and into every
// full-document config PUT for zero benefit. Connection is global (one OBS
// per machine); the scene map is per package.

import { ObsSocket, CLOSE_AUTH_FAILED } from './obsWebsocket.js';

const CONNECTION_STORAGE_KEY = 'streamgg.obs.connection';
const sceneMapStorageKey = (packageSlug) => `streamgg.obs.scenes.${packageSlug}`;

export const DEFAULT_OBS_URL = 'ws://127.0.0.1:4455';

const BACKOFF_MS = [1000, 2000, 4000, 8000, 15000];
const LOG_CAP = 50;

// Chrome asks before a website may reach apps on the operator's own PC or
// network (Local Network Access, WebSockets included since Chrome 147).
// Refused, the socket closes within milliseconds and nothing reaches OBS, so
// a failed attempt checks the permission before blaming OBS. Chrome 145+ has
// two settings, "Apps on device" (loopback-network: OBS on this PC) and
// "Local network" (local-network: other machines); 142–144 had the one
// "Local network access". A browser without them throws on the name and
// skips the check.
const BROWSER_ACCESS = {
  loopback: [
    { name: 'loopback-network', label: 'Apps on device' },
    { name: 'local-network-access', label: 'Local network access' },
  ],
  lan: [
    { name: 'local-network', label: 'Local network' },
    { name: 'local-network-access', label: 'Local network access' },
  ],
};
// While Chrome's permission prompt is up the socket waits in CONNECTING; the
// usual 8 s connect timeout would close it under the operator's hand.
const PROMPT_CONNECT_TIMEOUT_MS = 60000;

// Kinds whose sound is a setting of their own: OBS answers mute requests for
// them either way, but its mixer lists them only with it on (a browser
// source's "Control audio via OBS", a game or window capture's "Capture
// Audio"), so the console's does the same. Unset means off.
const AUDIO_SETTING = { browser_source: 'reroute_audio', game_capture: 'capture_audio', window_capture: 'capture_audio' };

// An iPhone or iPad can't reach OBS at all: iOS blocks ws:// from an https
// page even to localhost (iPadOS calls itself a Mac with touch), so connect()
// makes no attempt there; such a console cuts through the OBS PC's tab
// instead (src/lib/obsRemote.js).
export const IOS_DEVICE = typeof navigator !== 'undefined'
  && (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

export const isLoopbackHost = (url) => {
  try {
    return ['localhost', '127.0.0.1', '[::1]'].includes(new URL(url).hostname);
  } catch {
    return true; // unparseable → the connect attempt will surface the error
  }
};

const readJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? null;
  } catch {
    return null;
  }
};
const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full / disabled — the panel just won't persist */
  }
};

class ObsControl {
  constructor() {
    const stored = readJson(CONNECTION_STORAGE_KEY);
    this.connection = {
      url: stored?.url || DEFAULT_OBS_URL,
      password: stored?.password || '',
      enabled: stored?.enabled === true,
    };
    this.status = this.connection.enabled ? 'disconnected' : 'disabled';
    this.packageSlug = null;
    this.sceneMap = {};
    this.scenes = [];
    this.currentProgramScene = null;
    this.currentPreviewScene = null;
    this.studioMode = false;
    // What OBS's mixer shows (refreshAudio): its global audio devices
    // (GetSpecialInputs: Desktop Audio, Mic/Aux), the export's whole sound,
    // then any other audio source on air, such as one the operator added to a
    // scene: [{ name, role: 'mic' | 'desktop' | 'source', muted, volumeDb }].
    this.audio = [];
    // The global mic's device, once the stage has picked it for the package
    // (pickMicDevice): { input, model, device, matched } — the input's name,
    // the mic the package wants, the name of the Windows device the input is
    // on now, and whether that is the one. Null until picked.
    this.mic = null;
    this._micWanted = null;
    this._micDevices = [];
    this.logEntries = [];
    // Chrome's answer on whether this page may reach the OBS address:
    // { label, state } (BROWSER_ACCESS), null where the browser has no such
    // permission or hasn't answered yet.
    this.browserAccess = null;

    this._socket = null;
    this._retryTimer = null;
    this._attempt = 0;
    this._manualDisconnect = false;
    this._logSeq = 0;
    this._access = new Map(); // kind of address → Promise<{ kind, label, status } | null>
    this._failSeq = 0;
    this._blockLogged = false;
    this._iosLogged = false;
    this._audioSeq = 0;
    this._audioTimer = null;
    this._listeners = new Set();
    this._eventListeners = new Set();
    this._connectedListeners = new Set();
    // Level meters live apart from the snapshot: they change ~20 times a
    // second, and only the meter bars should repaint for them.
    this._meters = {};
    this._meterListeners = new Set();
    this._meterEmittedAt = 0;
    this._snap = null;
    this._rebuildSnapshot();

    // Bound so the component can hand them straight to useSyncExternalStore.
    this.subscribe = this.subscribe.bind(this);
    this.getSnapshot = this.getSnapshot.bind(this);
    this.subscribeMeters = this.subscribeMeters.bind(this);
    this.getMeters = this.getMeters.bind(this);

    // Asked now so the first attempt already knows whether Chrome's prompt
    // is pending (the longer connect timeout).
    if (this.connection.enabled) this._readBrowserAccess();
  }

  // ── subscription (useSyncExternalStore contract) ─────────────────────────

  subscribe(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  getSnapshot() {
    return this._snap;
  }

  _rebuildSnapshot() {
    this._snap = {
      status: this.status,
      connection: { ...this.connection },
      sceneMap: { ...this.sceneMap },
      scenes: this.scenes,
      currentProgramScene: this.currentProgramScene,
      currentPreviewScene: this.currentPreviewScene,
      studioMode: this.studioMode,
      audio: this.audio,
      mic: this.mic,
      browserAccess: this.browserAccess,
      log: this.logEntries,
    };
  }

  _emit() {
    this._rebuildSnapshot();
    for (const listener of this._listeners) listener();
  }

  // { inputName → { level, peak } } in dB (see _applyMeters), for the meter
  // bars; a separate useSyncExternalStore pair so a level change repaints
  // only them.
  subscribeMeters(listener) {
    this._meterListeners.add(listener);
    return () => this._meterListeners.delete(listener);
  }

  getMeters() {
    return this._meters;
  }

  // Collaborators (obsStage) hear every OBS event and every (re)connect, so
  // they can follow what the operator changes in OBS and re-apply their own
  // state to a freshly opened OBS. Both return an unsubscribe.
  onObsEvent(listener) {
    this._eventListeners.add(listener);
    return () => this._eventListeners.delete(listener);
  }

  onConnected(listener) {
    this._connectedListeners.add(listener);
    return () => this._connectedListeners.delete(listener);
  }

  // ── config ───────────────────────────────────────────────────────────────

  saveConnection(next) {
    const prev = this.connection;
    this.connection = {
      url: (next.url || '').trim() || DEFAULT_OBS_URL,
      password: next.password ?? '',
      enabled: next.enabled === true,
    };
    writeJson(CONNECTION_STORAGE_KEY, this.connection);

    if (!this.connection.enabled) {
      this._log(true, 'OBS control disabled — commands are no-ops');
      this.disconnect();
      this.status = 'disabled';
      this._emit();
      return;
    }
    const endpointChanged =
      prev.url !== this.connection.url || prev.password !== this.connection.password;
    if (this.status === 'disabled') this.status = 'disconnected';
    if (endpointChanged || !this.isConnected) {
      // Fresh settings deserve a fresh attempt — also the recovery path out
      // of 'auth-failed' and 'blocked', which never auto-retry on their own.
      this.disconnect();
      this._manualDisconnect = false;
      this._attempt = 0;
      this.connect();
    }
    this._emit();
  }

  // Loads (or seeds) the scene map for a package and makes it the active one
  // key resolution uses. Defaults map overlay slug → overlay display name —
  // exactly the scene names buildObsSceneCollection() exports, so a collection
  // imported from "Export to OBS" matches without any typing. Stored overrides
  // win; stored keys for revoked overlays are dropped.
  activateSceneMap(packageSlug, defaults) {
    this.packageSlug = packageSlug;
    const stored = readJson(sceneMapStorageKey(packageSlug)) ?? {};
    this.sceneMap = Object.fromEntries(
      Object.entries(defaults).map(([key, name]) => [key, stored[key] ?? name]),
    );
    this._emit();
  }

  saveSceneMap(nextMap) {
    if (!this.packageSlug) return;
    this.sceneMap = { ...nextMap };
    writeJson(sceneMapStorageKey(this.packageSlug), this.sceneMap);
    this._emit();
  }

  // ── connection lifecycle ─────────────────────────────────────────────────

  get isConnected() {
    return this.status === 'connected' && this._socket?.isOpen === true;
  }

  // Called by the panel on mount. Idempotent: StrictMode's double effect and
  // tab revisits all funnel here without spawning parallel sockets.
  connect() {
    if (!this.connection.enabled) return;
    if (IOS_DEVICE) {
      // Nothing to try (IOS_DEVICE); said once, so the log explains the
      // tiles cutting through the PC instead.
      if (!this._iosLogged) {
        this._iosLogged = true;
        this._log(false, "This iPhone or iPad can't connect to OBS itself (iOS blocks it): its tiles cut through the console on the OBS PC");
        this._emit();
      }
      return;
    }
    if (this._socket || this._retryTimer) return;
    this._manualDisconnect = false;
    this._openSocket();
  }

  disconnect() {
    this._manualDisconnect = true;
    if (this._retryTimer) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
    if (this._socket) {
      this._socket.close();
      this._socket = null;
    }
    if (this.status !== 'disabled') this.status = 'disconnected';
    this._emit();
  }

  _openSocket() {
    this.status = 'connecting';
    this._emit();
    this._readBrowserAccess();
    const socket = new ObsSocket({
      url: this.connection.url,
      password: this.connection.password,
      connectTimeoutMs: this.browserAccess?.state === 'prompt' ? PROMPT_CONNECT_TIMEOUT_MS : undefined,
      onEvent: (type, data) => this._onEvent(type, data),
      onClose: ({ code }) => this._onClose(socket, code),
    });
    this._socket = socket;
    socket
      .connect()
      .then(async () => {
        this._attempt = 0;
        this._blockLogged = false;
        this.status = 'connected';
        this._log(true, `Connected to OBS at ${this.connection.url}`);
        this._emit();
        await this.refreshScenes();
        await this.refreshAudio();
        for (const listener of this._connectedListeners) {
          try {
            listener();
          } catch (err) {
            console.warn('[obs] connected listener failed', err);
          }
        }
      })
      .catch(() => {
        /* teardown + retry are handled in _onClose */
      });
  }

  _onClose(socket, code) {
    if (socket !== this._socket) return; // a superseded socket's late close
    this._socket = null;
    const wasConnected = this.status === 'connected';
    this._setMeters({});

    if (code === CLOSE_AUTH_FAILED) {
      // Wrong password: retrying would just hammer OBS with bad auth.
      // saveConnection() with new credentials is the way back in.
      this.status = 'auth-failed';
      this._log(false, 'OBS rejected the password — check WebSocket Server Settings');
      this._emit();
      return;
    }
    if (this._manualDisconnect || !this.connection.enabled) {
      this.status = this.connection.enabled ? 'disconnected' : 'disabled';
      this._emit();
      return;
    }
    this.status = 'disconnected';
    if (wasConnected) {
      this._log(false, 'Lost connection to OBS — retrying');
      this._scheduleReconnect();
      this._emit();
      return;
    }
    // A failed attempt: before blaming OBS, ask whether the browser refused it.
    const url = this.connection.url;
    const seq = ++this._failSeq;
    this._emit();
    this._browserAccessFor(url).then((access) => {
      // Superseded meanwhile: new settings, a connect or a disconnect.
      if (seq !== this._failSeq || this.status !== 'disconnected' || this._socket || this._retryTimer) return;
      if (this._manualDisconnect || !this.connection.enabled || url !== this.connection.url) return;
      this._showBrowserAccess(access);
      if (access?.status.state === 'denied') {
        // Retrying can't get past Chrome. Its permission changing reconnects
        // (_onBrowserAccessChange), and so does Apply & connect.
        this.status = 'blocked';
        if (!this._blockLogged) {
          this._blockLogged = true;
          this._log(false, `Chrome is blocking this site from reaching ${url}: allow "${access.label}" in Chrome's site settings`);
        }
      } else {
        if (this._attempt === 0) {
          // First miss of a retry run only — the backoff loop itself stays
          // quiet so an OBS-closed-overnight console doesn't flood the log.
          this._log(false, `Can't reach OBS at ${url} — will keep retrying`);
        }
        this._scheduleReconnect();
      }
      this._emit();
    });
  }

  _scheduleReconnect() {
    const delay = BACKOFF_MS[Math.min(this._attempt, BACKOFF_MS.length - 1)];
    this._attempt += 1;
    this._retryTimer = setTimeout(() => {
      this._retryTimer = null;
      if (this.connection.enabled && !this._manualDisconnect && !this._socket) {
        this._openSocket();
      }
    }, delay);
  }

  // ── the browser's own gate (BROWSER_ACCESS) ──────────────────────────────

  // The permission Chrome checks before this page may reach `url`, read once
  // per kind of address and followed from then on, so allowing it in
  // Chrome's site settings reconnects a blocked console by itself.
  _browserAccessFor(url) {
    const kind = isLoopbackHost(url) ? 'loopback' : 'lan';
    if (!this._access.has(kind)) this._access.set(kind, this._queryBrowserAccess(kind));
    return this._access.get(kind);
  }

  async _queryBrowserAccess(kind) {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return null;
    for (const { name, label } of BROWSER_ACCESS[kind]) {
      let status;
      try {
        status = await navigator.permissions.query({ name });
      } catch {
        continue; // a name this browser doesn't have
      }
      const access = { kind, label, status };
      status.addEventListener('change', () => this._onBrowserAccessChange(access));
      return access;
    }
    return null;
  }

  // Keeps the snapshot's browserAccess on the current address's permission.
  _readBrowserAccess() {
    const url = this.connection.url;
    this._browserAccessFor(url).then((access) => {
      if (url === this.connection.url && this._showBrowserAccess(access)) this._emit();
    });
  }

  _showBrowserAccess(access) {
    const next = access ? { label: access.label, state: access.status.state } : null;
    if (next?.label === this.browserAccess?.label && next?.state === this.browserAccess?.state) return false;
    this.browserAccess = next;
    return true;
  }

  _onBrowserAccessChange(access) {
    if (access.kind !== (isLoopbackHost(this.connection.url) ? 'loopback' : 'lan')) return;
    this._showBrowserAccess(access);
    if (this.status === 'blocked' && access.status.state !== 'denied') {
      this._blockLogged = false;
      this._log(true, `Chrome now lets this site reach ${this.connection.url} — connecting`);
      this._attempt = 0;
      this.status = 'disconnected';
      this.connect();
    }
    this._emit();
  }

  // ── commands (never throw; always log key → resolved name → outcome) ────

  async setScene(sceneKey) {
    return this._switch(sceneKey, 'program cut', async (sceneName) => {
      await this._socket.request('SetCurrentProgramScene', { sceneName });
    });
  }

  // Studio Mode two-step: stage on preview, then run the transition. Refuses
  // (false, logged) when Studio Mode is off — the panel only routes clicks
  // here while Studio Mode is on, so this guard just covers the race where
  // OBS left Studio Mode and the event hasn't landed yet.
  async previewAndTake(sceneKey) {
    if (!this.studioMode) {
      this._log(false, `switch "${sceneKey}": FAILED — Studio Mode is off`);
      return false;
    }
    return this._switch(sceneKey, 'preview + take', async (sceneName) => {
      await this._socket.request('SetCurrentPreviewScene', { sceneName });
      await this._socket.request('TriggerStudioModeTransition');
    });
  }

  async _switch(sceneKey, mode, run) {
    const sceneName = this.sceneMap[sceneKey];
    if (!sceneName) {
      this._log(false, `switch "${sceneKey}": FAILED — no scene name mapped`);
      return false;
    }
    if (!this.isConnected) {
      this._log(false, `switch "${sceneKey}" → "${sceneName}": FAILED — not connected`);
      return false;
    }
    try {
      await run(sceneName);
      this._log(true, `switch "${sceneKey}" → "${sceneName}": OK (${mode})`);
      return true;
    } catch (err) {
      this._log(false, `switch "${sceneKey}" → "${sceneName}": FAILED — ${err.comment || err.message}`);
      return false;
    }
  }

  // ── scene items and sound (never throw; failures logged) ────────────────

  // The id OBS gives a source's item in a scene, or null when the scene has
  // no such source (a collection not made by the export, or renamed in OBS).
  async sceneItemId(sceneName, sourceName) {
    if (!this.isConnected || !sceneName || !sourceName) return null;
    try {
      const res = await this._socket.request('GetSceneItemId', { sceneName, sourceName });
      return res.sceneItemId ?? null;
    } catch {
      return null;
    }
  }

  async getSceneItemEnabled(sceneName, sourceName) {
    const sceneItemId = await this.sceneItemId(sceneName, sourceName);
    if (sceneItemId == null) return null;
    try {
      const res = await this._socket.request('GetSceneItemEnabled', { sceneName, sceneItemId });
      return res.sceneItemEnabled === true;
    } catch {
      return null;
    }
  }

  // Shows or hides one source in one scene. Resolves false (logged) when the
  // scene has no such source or OBS refuses.
  async setSceneItemEnabled(sceneName, sourceName, enabled) {
    if (!this.isConnected) return false;
    const sceneItemId = await this.sceneItemId(sceneName, sourceName);
    if (sceneItemId == null) {
      this.note(false, `"${sourceName}" isn't in the scene "${sceneName}"`);
      return false;
    }
    try {
      await this._socket.request('SetSceneItemEnabled', { sceneName, sceneItemId, sceneItemEnabled: enabled });
      return true;
    } catch (err) {
      this.note(false, `${enabled ? 'show' : 'hide'} "${sourceName}" in "${sceneName}": FAILED — ${err.comment || err.message}`);
      return false;
    }
  }

  // A source's place and size in a scene (obs-websocket's SceneItemTransform:
  // positionX/Y, boundsWidth/Height, …), or null when the scene has no such
  // source.
  async sceneItemTransform(sceneName, sourceName) {
    const sceneItemId = await this.sceneItemId(sceneName, sourceName);
    if (sceneItemId == null) return null;
    try {
      const res = await this._socket.request('GetSceneItemTransform', { sceneName, sceneItemId });
      return res.sceneItemTransform ?? null;
    } catch {
      return null;
    }
  }

  // Runs a batch of requests in OBS (see ObsSocket.requestBatch): OBS plays
  // it on its own clock, Sleeps included. Resolves true when every request
  // succeeded, false (logged) otherwise.
  async batch(label, requests, opts = {}) {
    if (!this.isConnected) return false;
    try {
      const results = await this._socket.requestBatch(requests, opts);
      const failed = results.filter((r) => r?.requestStatus && r.requestStatus.result === false);
      if (failed.length) {
        const why = failed[0].requestStatus.comment || `code ${failed[0].requestStatus.code}`;
        this.note(false, `${label}: ${failed.length} of ${requests.length} requests FAILED — ${why}`);
        return false;
      }
      return true;
    } catch (err) {
      this.note(false, `${label}: FAILED — ${err.comment || err.message}`);
      return false;
    }
  }

  // What OBS's mixer shows, with each one's mute and volume: the global audio
  // devices, mics first, then every other input with audio that is on air
  // (GetSourceActive), in OBS's input order. OBS refuses GetInputMute for an
  // input that can't have sound (an image, a colour), which is how those are
  // left out; the kinds in AUDIO_SETTING count only with their sound on.
  async refreshAudio() {
    if (!this.isConnected) return this.audio;
    const seq = ++this._audioSeq;
    try {
      const special = await this._socket.request('GetSpecialInputs');
      const globals = [['mic1', 'mic'], ['mic2', 'mic'], ['mic3', 'mic'], ['mic4', 'mic'], ['desktop1', 'desktop'], ['desktop2', 'desktop']]
        .filter(([key]) => special[key])
        .map(([key, role]) => ({ name: special[key], role }));
      const globalNames = new Set(globals.map((g) => g.name));
      let inputs = [];
      try {
        ({ inputs = [] } = await this._socket.request('GetInputList'));
      } catch {
        // The global devices still show.
      }
      const onAir = await Promise.all(inputs
        .filter(({ inputName }) => !globalNames.has(inputName))
        .map(async ({ inputName, unversionedInputKind: kind }) => {
          try {
            const [active] = await Promise.all([
              this._socket.request('GetSourceActive', { sourceName: inputName }),
              this._socket.request('GetInputMute', { inputName }),
            ]);
            if (!active.videoActive) return null;
            const setting = AUDIO_SETTING[kind];
            if (setting) {
              const { inputSettings } = await this._socket.request('GetInputSettings', { inputName });
              if (inputSettings?.[setting] !== true) return null;
            }
            return { name: inputName, role: 'source' };
          } catch {
            return null; // no audio, or gone meanwhile
          }
        }));
      const audio = await Promise.all([...globals, ...onAir.filter(Boolean)].map(async ({ name, role }) => {
        const [mute, volume] = await Promise.all([
          this._socket.request('GetInputMute', { inputName: name }),
          this._socket.request('GetInputVolume', { inputName: name }),
        ]);
        return { name, role, muted: mute.inputMuted === true, volumeDb: volume.inputVolumeDb };
      }));
      // A newer refresh (another scene went on air meanwhile) has the say.
      if (seq !== this._audioSeq) return this.audio;
      this.audio = audio;
    } catch (err) {
      if (seq !== this._audioSeq) return this.audio;
      this._log(false, `sound devices refresh FAILED — ${err.comment || err.message}`);
    }
    this._emit();
    return this.audio;
  }

  // A scene change sets several sources on and off air at once: one refresh
  // for the lot.
  _queueAudioRefresh() {
    clearTimeout(this._audioTimer);
    this._audioTimer = setTimeout(() => {
      this._audioTimer = null;
      this.refreshAudio();
    }, 150);
  }

  async setInputMuted(inputName, muted) {
    if (!this.isConnected) return false;
    try {
      await this._socket.request('SetInputMute', { inputName, inputMuted: muted });
      this.note(true, `${muted ? 'mute' : 'unmute'} "${inputName}": OK`);
      return true;
    } catch (err) {
      this.note(false, `${muted ? 'mute' : 'unmute'} "${inputName}": FAILED — ${err.comment || err.message}`);
      return false;
    }
  }

  // A volume drag sends many of these, so only failures go in the log; OBS's
  // InputVolumeChanged event brings the new value back into the snapshot.
  async setInputVolumeDb(inputName, volumeDb) {
    if (!this.isConnected) return false;
    try {
      await this._socket.request('SetInputVolume', { inputName, inputVolumeDb: volumeDb });
      return true;
    } catch (err) {
      this.note(false, `volume "${inputName}": FAILED — ${err.comment || err.message}`);
      return false;
    }
  }

  // Puts OBS's global mic (GetSpecialInputs' mic1, whatever the collection
  // names it) on the device a package's mic wants (MIC in obsExport.js),
  // picked by name among the devices OBS lists for the input
  // (GetInputPropertiesListPropertyItems on device_id: Default, then each
  // Windows input by its friendly name, with the id Windows gave it on this
  // PC, which is why the export can't carry it). With it not plugged in the
  // input is left as it is, and the log says what it stays on. Resolves
  // this.mic (null when OBS has no global mic, or the requests fail).
  async pickMicDevice(wanted) {
    if (!this.isConnected) return null;
    const { model, match, prefer } = wanted;
    try {
      const { mic1: inputName } = await this._socket.request('GetSpecialInputs');
      if (!inputName) {
        this.mic = null;
        this.note(false, `${model}: OBS has no Mic/Auxiliary Audio device (Settings > Audio)`);
        this._emit();
        return null;
      }
      const [{ propertyItems = [] }, { inputSettings = {} }] = await Promise.all([
        this._socket.request('GetInputPropertiesListPropertyItems', { inputName, propertyName: 'device_id' }),
        this._socket.request('GetInputSettings', { inputName }),
      ]);
      this._micWanted = wanted;
      this._micDevices = propertyItems.map((p) => ({ id: String(p.itemValue), name: String(p.itemName) }));
      const current = inputSettings.device_id ?? 'default';
      const hits = this._micDevices.filter((d) => d.id !== 'default' && match.test(d.name));
      const found = (prefer && hits.find((d) => prefer.test(d.name))) || hits[0] || null;
      if (!found) {
        this.note(false, `"${inputName}": no ${model} among OBS's mic devices — stays on "${this._micDeviceName(current)}"`);
      } else if (found.id === current) {
        this.note(true, `"${inputName}" is on the ${model} ("${found.name}")`);
      } else {
        await this._socket.request('SetInputSettings', { inputName, inputSettings: { device_id: found.id }, overlay: true });
        this.note(true, `"${inputName}" → ${model} ("${found.name}", was "${this._micDeviceName(current)}")`);
      }
      this._setMic(inputName, found ? found.id : current);
    } catch (err) {
      this.mic = null;
      this.note(false, `${model}: FAILED — ${err.comment || err.message}`);
      this._emit();
    }
    return this.mic;
  }

  _micDeviceName(id) {
    return this._micDevices.find((d) => d.id === id)?.name ?? id;
  }

  _setMic(input, deviceId) {
    const { model, match } = this._micWanted;
    const device = this._micDeviceName(deviceId);
    this.mic = { input, model, device, matched: deviceId !== 'default' && match.test(device) };
    this._emit();
  }

  // The mic's device changed in OBS itself (Settings > Audio): the strip's
  // tag follows. A device the pick never listed (plugged in since) is named
  // from a fresh list.
  async _followMicDevice(deviceId) {
    const input = this.mic.input;
    if (!this._micDevices.some((d) => d.id === deviceId) && this._socket) {
      try {
        const { propertyItems = [] } = await this._socket.request('GetInputPropertiesListPropertyItems', { inputName: input, propertyName: 'device_id' });
        this._micDevices = propertyItems.map((p) => ({ id: String(p.itemValue), name: String(p.itemName) }));
      } catch {
        // Named by its id, then.
      }
    }
    if (this.mic?.input === input) this._setMic(input, deviceId);
  }

  _patchAudio(inputName, patch) {
    if (!this.audio.some((a) => a.name === inputName)) return false;
    this.audio = this.audio.map((a) => (a.name === inputName ? { ...a, ...patch } : a));
    return true;
  }

  // InputVolumeMeters: each input's channels as [magnitude, peak, input peak]
  // multipliers; a meter shows the loudest channel's magnitude as its bar and
  // its peak as a tick, as OBS's mixer does: { inputName → { level, peak } }
  // in dB (−Infinity when silent). Repainted at most every 60 ms.
  _applyMeters(inputs) {
    const toDb = (mul) => (mul > 0 ? 20 * Math.log10(mul) : -Infinity);
    const next = { ...this._meters };
    for (const input of inputs ?? []) {
      const channels = input.inputLevelsMul ?? [];
      next[input.inputName] = {
        level: toDb(Math.max(0, ...channels.map((ch) => ch?.[0] ?? 0))),
        peak: toDb(Math.max(0, ...channels.map((ch) => ch?.[1] ?? 0))),
      };
    }
    const now = Date.now();
    if (now - this._meterEmittedAt < 60) {
      this._meters = next;
      return;
    }
    this._meterEmittedAt = now;
    this._setMeters(next);
  }

  _setMeters(next) {
    this._meters = next;
    for (const listener of this._meterListeners) listener();
  }

  // Pulls the live scene list + studio mode state. Returns [] on any failure
  // (never throws) — callers treat an empty list as "nothing to show yet".
  async refreshScenes() {
    if (!this.isConnected) return [];
    try {
      const [sceneList, studio] = await Promise.all([
        this._socket.request('GetSceneList'),
        this._socket.request('GetStudioModeEnabled'),
      ]);
      this._applySceneList(sceneList);
      this.studioMode = studio.studioModeEnabled === true;
      this.currentProgramScene = sceneList.currentProgramSceneName ?? null;
      this.currentPreviewScene = sceneList.currentPreviewSceneName ?? null;
      this._emit();
      return this.scenes;
    } catch (err) {
      this._log(false, `scene list refresh FAILED — ${err.comment || err.message}`);
      return [];
    }
  }

  _applySceneList({ scenes }) {
    // GetSceneList orders index 0 = bottom of the OBS UI list; reverse so the
    // datalist reads top-to-bottom like the OBS sidebar.
    this.scenes = (scenes ?? [])
      .map((s) => s.sceneName)
      .filter(Boolean)
      .reverse();
  }

  _onEvent(type, data) {
    let changed = true;
    switch (type) {
      case 'CurrentProgramSceneChanged':
        this.currentProgramScene = data.sceneName ?? null;
        this._queueAudioRefresh();
        break;
      case 'CurrentPreviewSceneChanged':
        this.currentPreviewScene = data.sceneName ?? null;
        break;
      case 'StudioModeStateChanged':
        this.studioMode = data.studioModeEnabled === true;
        // Leaving Studio Mode clears OBS's preview scene.
        if (!this.studioMode) this.currentPreviewScene = null;
        break;
      case 'SceneListChanged':
        this._applySceneList(data);
        break;
      case 'InputMuteStateChanged':
        changed = this._patchAudio(data.inputName, { muted: data.inputMuted === true });
        break;
      case 'InputVolumeChanged':
        changed = this._patchAudio(data.inputName, { volumeDb: data.inputVolumeDb });
        break;
      case 'InputNameChanged':
        changed = this._patchAudio(data.oldInputName, { name: data.inputName });
        if (this.mic && data.oldInputName === this.mic.input) {
          this.mic = { ...this.mic, input: data.inputName };
          changed = true;
        }
        break;
      case 'InputSettingsChanged':
        if (this.mic && data.inputName === this.mic.input && data.inputSettings?.device_id != null) {
          this._followMicDevice(String(data.inputSettings.device_id));
        }
        changed = false;
        this._queueAudioRefresh();
        break;
      case 'InputCreated':
      case 'InputRemoved':
      case 'InputActiveStateChanged':
      case 'SceneItemCreated':
      case 'SceneItemRemoved':
      case 'SceneItemEnableStateChanged':
        // An audio source added or taken away (in a scene, or a global device
        // in OBS's Settings), one going on or off air, or its sound switched
        // on or off (AUDIO_SETTING): the mixer follows.
        changed = false;
        this._queueAudioRefresh();
        break;
      case 'InputVolumeMeters':
        changed = false;
        this._applyMeters(data.inputs);
        break;
      default:
        changed = false;
    }
    for (const listener of this._eventListeners) {
      try {
        listener(type, data);
      } catch (err) {
        console.warn('[obs] event listener failed', err);
      }
    }
    if (changed) this._emit();
  }

  // ── logging ──────────────────────────────────────────────────────────────

  // Collaborators (the auto winner-screen director) append to the same
  // activity log the panel shows — one place answers "why did my scene just
  // change". Unlike _log (whose callers all emit on their own), note() must
  // emit itself: a note not followed by any OBS traffic (the director's
  // leaving-scenes-alone path) would otherwise sit invisible until the next
  // scene event repainted the panel.
  note(ok, text) {
    this._log(ok, text);
    this._emit();
  }

  _log(ok, text) {
    this._logSeq += 1;
    this.logEntries = [
      { id: this._logSeq, ts: Date.now(), ok, text },
      ...this.logEntries.slice(0, LOG_CAP - 1),
    ];
    console[ok ? 'info' : 'warn'](`[obs] ${text}`);
  }
}

export const obsControl = new ObsControl();
